// The "source packet" for Generate (spec §3.3): the client's Ops Hub facts for the update period,
// capped and trimmed so a long history never blows the request. Built in the browser from data
// the client page already has; the proxy owns the prompt. Ids are "<kind>:<id>" so the proxy can
// map what Claude cites back to labels for the internal "Built from" list.
import { localDate, addDays } from "./period.js";
import { STAGE_LABELS, isActiveStage } from "../proposals/stages.js";
import { splitObjectiveTasks } from "../clients/clientLogic.js";
import { clientVisibleWork } from "./privacy.js";
import { visibleCrm } from "./crmBlock.js";

export const PACKET_CHAR_CAP = 40000;
const NOTE_CHARS = 1500;
const LIMITS = { completedTasks: 60, inProgressTasks: 40, upcomingTasks: 40, objectives: 30, proposals: 30, notes: 30, previousNextSteps: 20 };
const day = (iso) => (iso ? (/^\d{4}-\d{2}-\d{2}$/.test(iso) ? iso : localDate(new Date(iso))) : null);
const inRange = (d, a, b) => !!d && d >= a && d <= b;

export function buildPacket({ clientName, meeting, objectives: allObjectives = [], tasks: allTasks = [], proposals = [], notes = [], previousMeeting = null, files = [], emails = [], crm = null }) {
  // Private work never reaches the model.
  const { objectives, tasks } = clientVisibleWork(allObjectives, allTasks);
  const { periodStart: start, periodEnd: end } = meeting;
  const upcomingEnd = addDays(end, 30);
  const objs = objectives.filter((o) => !meeting.projectId || o.projectId === meeting.projectId);
  const objName = new Map(objectives.map((o) => [o.id, o.name]));
  const objectiveOf = (t) => objName.get(t.objectiveId) || null;
  // A project meeting only reports that project's work: a task's own projectId decides; a task
  // without one belongs to its objective's project.
  const objProject = new Map(objectives.map((o) => [o.id, o.projectId]));
  const inScope = (t) => !meeting.projectId || (t.projectId ? t.projectId === meeting.projectId : objProject.get(t.objectiveId) === meeting.projectId);
  const scopedTasks = tasks.filter(inScope);

  const completedTasks = scopedTasks
    .filter((t) => t.status === "Completed" && inRange(day(t.completedAt), start, end))
    .map((t) => ({ id: `task:${t.id}`, name: t.name, completedOn: day(t.completedAt), objective: objectiveOf(t) }))
    .sort((a, b) => (a.completedOn < b.completedOn ? 1 : -1));
  const inProgressTasks = scopedTasks
    .filter((t) => t.status === "In Progress" || t.status === "Blocked")
    .map((t) => ({ id: `task:${t.id}`, name: t.name, status: t.status, dueDate: t.dueDate || null, objective: objectiveOf(t) }));
  const inProgressIds = new Set(inProgressTasks.map((t) => t.id));
  const upcomingTasks = scopedTasks
    .filter((t) => t.status !== "Completed" && t.dueDate && t.dueDate > end && t.dueDate <= upcomingEnd && !inProgressIds.has(`task:${t.id}`))
    .map((t) => ({ id: `task:${t.id}`, name: t.name, dueDate: t.dueDate, objective: objectiveOf(t) }))
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : 1));
  const objectivesOut = objs.map((o) => {
    const its = scopedTasks.filter((t) => t.objectiveId === o.id);
    return { id: `objective:${o.id}`, name: o.name, done: splitObjectiveTasks(its).completed.length, total: its.length };
  });
  const proposalsOut = proposals
    .map((p) => {
      const changes = (p.stageHistory || []).filter((h) => inRange(day(h.enteredAt), start, end)).map((h) => STAGE_LABELS[h.stage] || h.stage);
      return { p, changes };
    })
    .filter(({ p, changes }) => isActiveStage(p.stage) || changes.length)
    .map(({ p, changes }) => ({ id: `proposal:${p.id}`, name: p.topic || "(untitled proposal)", stage: p.stage, stageLabel: STAGE_LABELS[p.stage] || p.stage, dueDate: p.dueDate || null, changesInPeriod: changes }));
  const notesOut = notes
    .filter((n) => inRange(day(n.createdAt), start, end))
    .sort((a, b) => (a.createdAt === b.createdAt ? 0 : a.createdAt < b.createdAt ? 1 : -1))
    .map((n) => ({ id: `note:${n.id}`, date: day(n.createdAt), text: String(n.text || "").slice(0, NOTE_CHARS) }));
  const filesOut = files.slice(0, 40);
  const emailsOut = [...emails].sort((a, b) => (a.date === b.date ? 0 : a.date < b.date ? 1 : -1)).slice(0, 60);
  const nextSteps = (previousMeeting?.sections || []).find((s) => s.kind !== "agenda" && /^next steps$/i.test(s.title || ""));
  const previousNextSteps = (nextSteps?.items || []).map((i) => ({
    id: `meeting:${previousMeeting.id}:${i.id}`,
    text: [i.headline, i.body].filter(Boolean).join(" — ").slice(0, 600),
    owner: i.owner?.type === "client" ? "client" : i.owner ? "sawtooth" : null,
    dueDate: i.dueDate || null,
  }));

  // CRM outreach (CRM spec 2026-11-03): only what the client could see in this meeting —
  // visibleCrm drops hidden entries/blocks and carries name/organization/role, never email/phone.
  const crmBlock = visibleCrm(crm, { type: meeting.type, crm: meeting.crm });
  const crmEvents = (crmBlock?.events || []).slice(0, 40).map((e) => {
    const raw = (crm.events || []).find((x) => x.id === e.id) || {};
    return { id: `crm:${e.id}`, date: e.date, kind: String(raw.kind || ""), name: String(raw.name || ""), organization: String(raw.organization || ""), role: String(raw.role || ""),
      ...(raw.kind === "activity" ? { text: e.text.slice(0, NOTE_CHARS) } : { status: String(raw.statusLabel || raw.status || "") }) };
  });

  const packet = {
    client: { name: clientName || "" },
    meeting: { number: meeting.number, meetingDate: meeting.meetingDate, periodStart: start, periodEnd: end },
    completedTasks: completedTasks.slice(0, LIMITS.completedTasks),
    inProgressTasks: inProgressTasks.slice(0, LIMITS.inProgressTasks),
    upcomingTasks: upcomingTasks.slice(0, LIMITS.upcomingTasks),
    objectives: objectivesOut.slice(0, LIMITS.objectives),
    proposals: proposalsOut.slice(0, LIMITS.proposals),
    notes: notesOut.slice(0, LIMITS.notes),
    files: filesOut,
    emails: emailsOut,
    previousNextSteps: previousNextSteps.slice(0, LIMITS.previousNextSteps),
    crmCounts: crmBlock ? crmBlock.counts : null,
    crmEvents,
    trimmed: false,
  };
  const over = (x) => JSON.stringify(x).length > PACKET_CHAR_CAP;
  packet.trimmed = notesOut.length > LIMITS.notes || completedTasks.length > LIMITS.completedTasks || emails.length > 60 || files.length > 40;
  // Oldest data goes first: emails, then notes (both newest-first → pop from the end), then
  // completed tasks, then files last (also most-recent-first → pop removes the oldest).
  while (over(packet) && packet.emails.length) { packet.emails.pop(); packet.trimmed = true; }
  while (over(packet) && packet.notes.length) { packet.notes.pop(); packet.trimmed = true; }
  while (over(packet) && packet.completedTasks.length) { packet.completedTasks.pop(); packet.trimmed = true; }
  while (over(packet) && packet.crmEvents.length) { packet.crmEvents.pop(); packet.trimmed = true; }
  while (over(packet) && packet.files.length) { packet.files.pop(); packet.trimmed = true; }
  return packet;
}
